import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import cytoscape from 'cytoscape';
import fcose from 'cytoscape-fcose';
import coseBilkent from 'cytoscape-cose-bilkent';
import { 
  Shield, 
  PieChart, 
  BarChart2, 
  AlertCircle,
  Layers,
  Target,
  Maximize2,
  Minimize2,
  X
} from 'lucide-react';
import { api } from '../services/api';

// Register Cytoscape extensions
try {
  cytoscape.use(fcose);
  cytoscape.use(coseBilkent);
} catch (e) {
  // Extension already registered
}

const CLUSTER_DEFINITIONS = [
  {
    id: 'cluster_frontend',
    name: 'Frontend, Web & UI/UX',
    category: 'Frontend/Design',
    color: '#3B82F6',
    description: 'User interface development, web components, styling, accessibility, and client experiences.',
    keywords: [
      'react', 'typescript', 'javascript', 'js', 'ui/ux', 'ux/ui', 'ui', 'ux', 'figma', 'tailwind',
      'tailwind css', 'd3', 'd3.js', 'react native', 'vue', 'angular', 'html', 'css', 'web', 'frontend',
      'design', 'prototyping', 'adobe', 'graphic', 'visual'
    ]
  },
  {
    id: 'cluster_backend',
    name: 'Backend, Cloud & Infrastructure',
    category: 'Backend/Infra',
    color: '#10B981',
    description: 'API services, databases, server infrastructure, cloud deployment, and system architecture.',
    keywords: [
      'python', 'fastapi', 'django', 'node.js', 'node', 'postgresql', 'postgres', 'redis', 'docker',
      'aws', 'gcp', 'azure', 'devops', 'kubernetes', 'k8s', 'sql', 'database', 'backend', 'api',
      'graphql', 'rest', 'server', 'linux', 'ci/cd', 'cloud', 'infrastructure'
    ]
  },
  {
    id: 'cluster_ai',
    name: 'AI, Machine Learning & Data',
    category: 'AI/Data',
    color: '#8B5CF6',
    description: 'Machine learning algorithms, data analytics, predictive modeling, NLP, and intelligent agents.',
    keywords: [
      'machine learning', 'data analysis', 'data science', 'nlp', 'pytorch', 'tensorflow', 'openai',
      'openai api', 'deep learning', 'pandas', 'analytics', 'ai', 'ml', 'llm', 'computer vision',
      'neural', 'ubex ai', 'ubex'
    ]
  },
  {
    id: 'cluster_qa',
    name: 'QA, Testing & Usability',
    category: 'QA/Testing',
    color: '#EC4899',
    description: 'Software verification, end-to-end testing, quality assurance, usability, and test automation.',
    keywords: [
      'testing', 'qa', 'manual testing', 'automation', 'selenium', 'cypress', 'jest', 'clicking stuff',
      'clicking on stuff', 'pressing save', 'being human', 'usability', 'quality', 'test', 'test2'
    ]
  },
  {
    id: 'cluster_business',
    name: 'Product, Agile & Business Strategy',
    category: 'Product/Business',
    color: '#F59E0B',
    description: 'Product vision, agile methodologies, customer discovery, pitch delivery, and team growth.',
    keywords: [
      'product strategy', 'product management', 'agile', 'scrum', 'user research', 'growth hacking',
      'stakeholder management', 'business', 'marketing', 'pitching', 'management', 'strategy', 'finance'
    ]
  }
];

const getSkillCluster = (skillName, skillCategory = '') => {
  const sName = (skillName || '').toLowerCase().trim();
  const sCat = (skillCategory || '').toLowerCase().trim();

  if (sCat.includes('front') || sCat.includes('design') || sCat.includes('mobile')) return 'cluster_frontend';
  if (sCat.includes('back') || sCat.includes('infra') || sCat.includes('database')) return 'cluster_backend';
  if (sCat.includes('ai') || sCat.includes('data') || sCat.includes('ml')) return 'cluster_ai';
  if (sCat.includes('qa') || sCat.includes('test')) return 'cluster_qa';
  if (sCat.includes('product') || sCat.includes('business')) return 'cluster_business';

  for (const def of CLUSTER_DEFINITIONS) {
    if (def.keywords.includes(sName)) return def.id;
  }

  if (sName.includes('test') || sName.includes('qa') || sName.includes('click') || sName.includes('save') || sName.includes('human')) {
    return 'cluster_qa';
  }
  if (sName.includes('react') || sName.includes('figma') || sName.includes('ux') || sName.includes('ui') || sName.includes('script') || sName.includes('frontend') || sName.includes('css')) {
    return 'cluster_frontend';
  }
  if (sName.includes('data') || sName.includes('learn') || sName.includes('ai') || sName.includes('ml') || sName.includes('torch') || sName.includes('ubex')) {
    return 'cluster_ai';
  }
  if (sName.includes('node') || sName.includes('sql') || sName.includes('python') || sName.includes('docker') || sName.includes('cloud') || sName.includes('api') || sName.includes('backend') || sName.includes('infra')) {
    return 'cluster_backend';
  }
  if (sName.includes('product') || sName.includes('agile') || sName.includes('scrum') || sName.includes('market') || sName.includes('business')) {
    return 'cluster_business';
  }

  return 'cluster_frontend';
};

const GraphExplorer = () => {
  const { isDarkMode } = useTheme();
  const { currentUser, isAdmin } = useAuth();
  const containerRef = useRef(null);
  const cyRef = useRef(null);

  const [selectedNode, setSelectedNode] = useState(null);
  const [activeTab, setActiveTab] = useState('graph'); // 'graph' or 'clusters'
  const [selectedClusterFilter, setSelectedClusterFilter] = useState('all');
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [filters, setFilters] = useState({
    showPeople: true,
    showSkills: true,
    showProjects: true,
    skillFilter: 'all'
  });

  // Pull real live data from server Neo4j database & storage
  const [allPeople, setAllPeople] = useState(() => JSON.parse(localStorage.getItem('skillmesh_people') || '[]'));
  const [allProjects, setAllProjects] = useState(() => JSON.parse(localStorage.getItem('skillmesh_projects') || '[]'));

  useEffect(() => {
    // Fetch live graph data from server Neo4j database
    api.getPeople().then(data => {
      if (Array.isArray(data)) setAllPeople(data);
    });
    api.getProjects().then(data => {
      if (Array.isArray(data)) setAllProjects(data);
    });

    const handleStorage = () => {
      setAllPeople(JSON.parse(localStorage.getItem('skillmesh_people') || '[]'));
      setAllProjects(JSON.parse(localStorage.getItem('skillmesh_projects') || '[]'));
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Ensure currentUser is always visible in the graph even right after registration
  const combinedPeople = useMemo(() => {
    const list = [...allPeople];
    if (currentUser && currentUser.email) {
      const exists = list.some(p => (p.email || '').toLowerCase() === currentUser.email.toLowerCase());
      if (!exists) {
        list.push({
          id: currentUser.id || `p_${currentUser.email}`,
          name: currentUser.name || currentUser.email.split('@')[0],
          role: currentUser.title || 'Platform Member',
          team: 'Engineering',
          email: currentUser.email,
          availability: currentUser.availability || 'Available',
          skills: (currentUser.skills || []).map(s => typeof s === 'string' ? s : s.name),
          rating: 5.0,
          experience: `${currentUser.experienceYears || 1} years`,
          bio: currentUser.bio || '',
          avatar: currentUser.avatar || 'dev-1'
        });
      }
    }
    return list;
  }, [allPeople, currentUser]);

  // Compute cluster data dynamically from real participants and projects
  const clustersData = useMemo(() => {
    return CLUSTER_DEFINITIONS.map(def => {
      // Find all people with at least one skill in this cluster
      const members = combinedPeople.filter(p => {
        const pSkills = (p.skills || []).map(s => typeof s === 'string' ? s : s?.name || '').filter(Boolean);
        return pSkills.some(s => getSkillCluster(s) === def.id);
      });

      // Find all project requirements in this cluster
      let demand = 0;
      allProjects.forEach(proj => {
        const reqSkills = (proj.requiredSkills || []).map(s => typeof s === 'string' ? s : s?.name || '').filter(Boolean);
        reqSkills.forEach(s => {
          if (getSkillCluster(s) === def.id) demand++;
        });
      });

      const coverageRatio = demand > 0
        ? `${Math.min(100, Math.round((members.length / demand) * 100))}%`
        : members.length > 0 ? '100%' : '0%';

      const presentSkills = new Set();
      members.forEach(p => {
        (p.skills || []).forEach(s => presentSkills.add((typeof s === 'string' ? s : s?.name || '').toLowerCase()));
      });
      const topMissing = def.keywords.filter(k => k.length > 2 && !presentSkills.has(k)).slice(0, 2);

      return {
        ...def,
        skills: def.keywords.slice(0, 8),
        peopleCount: members.length,
        projectDemand: demand,
        supplyDemandRatio: coverageRatio,
        topMissingSkills: topMissing
      };
    });
  }, [combinedPeople, allProjects, CLUSTER_DEFINITIONS]);

  // Compute program macro statistics dynamically
  const macroStats = useMemo(() => {
    const activeParticipants = combinedPeople.length;
    const skillDemandMap = {};
    allProjects.forEach(p => {
      (p.requiredSkills || []).forEach(s => {
        const sName = typeof s === 'string' ? s : s?.name;
        if (sName) {
          skillDemandMap[sName] = (skillDemandMap[sName] || 0) + 1;
        }
      });
    });
    const sortedBridgeSkills = Object.entries(skillDemandMap)
      .sort((a, b) => b[1] - a[1])
      .map(entry => entry[0]);
    const bridgeText = sortedBridgeSkills.slice(0, 2).join(' & ') || 'React & Python';

    const totalPossibleEdges = Math.max(1, (combinedPeople.length * allProjects.length));
    let actualConnections = 0;
    combinedPeople.forEach(person => {
      const pSkills = (person.skills || []).map(s => (typeof s === 'string' ? s : s?.name || '').toLowerCase());
      allProjects.forEach(proj => {
        const reqSkills = (proj.requiredSkills || []).map(s => (typeof s === 'string' ? s : s?.name || '').toLowerCase());
        if (pSkills.some(s => reqSkills.includes(s))) actualConnections++;
      });
    });
    const rawDensity = (actualConnections / totalPossibleEdges).toFixed(2);

    return {
      skillClusters: clustersData.length,
      activeParticipants,
      networkDensity: rawDensity,
      bridgeSkills: bridgeText
    };
  }, [combinedPeople, allProjects, clustersData]);

  // Base Graph Data - Dynamically generated from real active people and projects
  const graphData = useMemo(() => {
    const nodes = [];
    const edges = [];
    const skillSet = new Map(); // lowercase skill name -> skillNode

    // 1. Live People nodes
    combinedPeople.forEach(person => {
      const pId = `person_${person.id || person.email || person.name}`;
      const pSkills = (person.skills || []).map(s => typeof s === 'string' ? s : s?.name || '').filter(Boolean);
      
      const clusterCounts = {};
      const personClusterIds = new Set();
      pSkills.forEach(s => {
        const cId = getSkillCluster(s);
        clusterCounts[cId] = (clusterCounts[cId] || 0) + 1;
        personClusterIds.add(cId);
      });

      let dominantCluster = 'cluster_frontend';
      let maxCount = 0;
      Object.entries(clusterCounts).forEach(([cId, count]) => {
        if (count > maxCount) {
          maxCount = count;
          dominantCluster = cId;
        }
      });
      if (personClusterIds.size === 0) {
        const roleStr = `${person.role || ''} ${person.team || ''}`.toLowerCase();
        if (roleStr.includes('back') || roleStr.includes('infra') || roleStr.includes('devops')) dominantCluster = 'cluster_backend';
        else if (roleStr.includes('ai') || roleStr.includes('data')) dominantCluster = 'cluster_ai';
        else if (roleStr.includes('qa') || roleStr.includes('test')) dominantCluster = 'cluster_qa';
        else if (roleStr.includes('product') || roleStr.includes('manage')) dominantCluster = 'cluster_business';
        personClusterIds.add(dominantCluster);
      }

      nodes.push({
        id: pId,
        label: person.name,
        type: 'person',
        department: person.team || person.role || 'Engineering',
        clusterId: dominantCluster,
        clusterIds: Array.from(personClusterIds),
        skills: pSkills
      });

      // Track skills and add person-skill edge
      pSkills.forEach(sName => {
        const sKey = sName.toLowerCase().trim();
        if (!sKey) return;
        let sId;
        if (!skillSet.has(sKey)) {
          sId = `skill_${sKey.replace(/\s+/g, '_')}`;
          const clId = getSkillCluster(sName);
          const clObj = CLUSTER_DEFINITIONS.find(c => c.id === clId);
          const category = clObj ? clObj.category.split('/')[0] : 'General';
          const sNode = { id: sId, label: sName, type: 'skill', category, clusterId: clId };
          skillSet.set(sKey, sNode);
        } else {
          sId = skillSet.get(sKey).id;
        }

        edges.push({
          source: pId,
          target: sId,
          type: 'has_skill'
        });
      });
    });

    // 2. Live Project nodes
    allProjects.forEach(proj => {
      const prId = `project_${proj.id || proj.name}`;
      const reqSkills = (proj.requiredSkills || []).map(s => typeof s === 'string' ? s : s?.name || '').filter(Boolean);

      const clusterCounts = {};
      const projClusterIds = new Set();
      reqSkills.forEach(s => {
        const cId = getSkillCluster(s);
        clusterCounts[cId] = (clusterCounts[cId] || 0) + 1;
        projClusterIds.add(cId);
      });

      const pNameLower = (proj.name || '').toLowerCase();
      if (pNameLower.includes('ai') || pNameLower.includes('data') || pNameLower.includes('ubex')) projClusterIds.add('cluster_ai');
      if (pNameLower.includes('test')) projClusterIds.add('cluster_qa');
      if (pNameLower.includes('front') || pNameLower.includes('ui') || pNameLower.includes('ux')) projClusterIds.add('cluster_frontend');
      if (pNameLower.includes('back') || pNameLower.includes('api') || pNameLower.includes('cloud')) projClusterIds.add('cluster_backend');

      let dominantCluster = 'cluster_frontend';
      let maxCount = 0;
      Object.entries(clusterCounts).forEach(([cId, count]) => {
        if (count > maxCount) {
          maxCount = count;
          dominantCluster = cId;
        }
      });
      if (projClusterIds.size === 0) {
        projClusterIds.add(dominantCluster);
      } else if (maxCount === 0) {
        dominantCluster = Array.from(projClusterIds)[0];
      }

      nodes.push({
        id: prId,
        label: proj.name,
        type: 'project',
        status: proj.status || 'Planning',
        clusterId: dominantCluster,
        clusterIds: Array.from(projClusterIds),
        requiredSkills: reqSkills
      });

      // Track skills and add project-skill edge
      reqSkills.forEach(sName => {
        const sKey = sName.toLowerCase().trim();
        if (!sKey) return;
        let sId;
        if (!skillSet.has(sKey)) {
          sId = `skill_${sKey.replace(/\s+/g, '_')}`;
          const clId = getSkillCluster(sName);
          const clObj = CLUSTER_DEFINITIONS.find(c => c.id === clId);
          const category = clObj ? clObj.category.split('/')[0] : 'General';
          const sNode = { id: sId, label: sName, type: 'skill', category, clusterId: clId };
          skillSet.set(sKey, sNode);
        } else {
          sId = skillSet.get(sKey).id;
        }

        edges.push({
          source: prId,
          target: sId,
          type: 'requires_skill'
        });
      });
    });

    // 3. Add unique skill nodes
    skillSet.forEach(sNode => {
      nodes.push(sNode);
    });

    return { nodes, edges };
  }, [combinedPeople, allProjects, CLUSTER_DEFINITIONS]);

  // Filter graph elements
  const filteredData = useMemo(() => {
    if (!graphData) return null;

    const visibleNodes = graphData.nodes.filter(node => {
      if (!filters.showPeople && node.type === 'person') return false;
      if (!filters.showSkills && node.type === 'skill') return false;
      if (!filters.showProjects && node.type === 'project') return false;

      if (selectedClusterFilter !== 'all') {
        if (node.type === 'skill') {
          if (node.clusterId !== selectedClusterFilter) return false;
        } else {
          const nodeClusters = node.clusterIds || [node.clusterId];
          if (!nodeClusters.includes(selectedClusterFilter)) return false;
        }
      }

      if (node.type === 'skill' && filters.skillFilter !== 'all') {
        const categoryMap = {
          'frontend': 'Frontend',
          'backend': 'Backend', 
          'design': 'Design',
          'business': 'Business',
          'ai': 'AI/Data',
          'qa': 'QA/Testing'
        };
        if (node.category !== categoryMap[filters.skillFilter]) return false;
      }

      return true;
    });

    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));

    const visibleEdges = graphData.edges.filter(edge => {
      return visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target);
    });

    return {
      nodes: visibleNodes,
      edges: visibleEdges
    };
  }, [graphData, filters, selectedClusterFilter]);

  // Cytoscape initialization
  useEffect(() => {
    if (activeTab !== 'graph' || !filteredData || !containerRef.current) return;

    if (cyRef.current) {
      try {
        cyRef.current.destroy();
      } catch (e) {
        // Ignore destruction errors
      }
      cyRef.current = null;
    }

    try {
      const cy = cytoscape({
        container: containerRef.current,
        elements: {
          nodes: filteredData.nodes.map(node => ({
            data: {
              id: node.id,
              label: node.label,
              type: node.type,
              department: node.department,
              category: node.category,
              status: node.status,
              skills: node.skills,
              clusterId: node.clusterId
            }
          })),
          edges: filteredData.edges.map(edge => ({
            data: {
              id: `${edge.source}-${edge.target}`,
              source: edge.source,
              target: edge.target,
              type: edge.type
            }
          }))
        },
        style: [
          {
            selector: 'node',
            style: {
              label: 'data(label)',
              'font-size': 11,
              color: isDarkMode ? '#ffffff' : '#1e293b',
              'text-wrap': 'wrap',
              'text-valign': 'center',
              'text-halign': 'center',
              width: 34,
              height: 34,
              'text-outline-color': isDarkMode ? '#0f172a' : '#ffffff',
              'text-outline-width': 2,
              'background-color': '#94a3b8',
              'border-width': 2,
              'border-color': isDarkMode ? '#ffffff33' : '#e2e8f0'
            }
          },
          {
            selector: 'node[type = "person"]',
            style: {
              'background-color': '#2563EB', // Blue
              width: 42,
              height: 42,
              'font-size': 11,
              'font-weight': 'bold'
            }
          },
          {
            selector: 'node[type = "skill"]',
            style: {
              'background-color': '#10B981', // Emerald
              width: 36,
              height: 36,
              'font-size': 10
            }
          },
          {
            selector: 'node[type = "project"]',
            style: {
              'background-color': '#6366F1', // Indigo
              shape: 'round-rectangle',
              width: 140,
              height: 36,
              'font-size': 10
            }
          },
          // Cluster specific coloring for skills
          {
            selector: 'node[clusterId = "cluster_frontend"]',
            style: { 'border-color': '#3B82F6', 'border-width': 3 }
          },
          {
            selector: 'node[clusterId = "cluster_backend"]',
            style: { 'border-color': '#10B981', 'border-width': 3 }
          },
          {
            selector: 'node[clusterId = "cluster_ai"]',
            style: { 'border-color': '#8B5CF6', 'border-width': 3 }
          },
          {
            selector: 'node[clusterId = "cluster_qa"]',
            style: { 'border-color': '#EC4899', 'border-width': 3 }
          },
          {
            selector: 'node[clusterId = "cluster_business"]',
            style: { 'border-color': '#F59E0B', 'border-width': 3 }
          },
          {
            selector: 'edge[type = "has_skill"]',
            style: {
              width: 1.5,
              'line-color': isDarkMode ? '#64748B' : '#94A3B8',
              'target-arrow-shape': 'none'
            }
          },
          {
            selector: 'edge[type = "requires_skill"]',
            style: {
              width: 2.2,
              'line-color': '#F59E0B',
              'target-arrow-shape': 'triangle',
              'target-arrow-color': '#F59E0B'
            }
          }
        ],
        layout: {
          name: 'fcose',
          fit: true,
          animate: false,
          padding: 40
        }
      });

      cy.on('tap', 'node', (evt) => {
        const node = evt.target;
        setSelectedNode({
          id: node.id(),
          label: node.data('label'),
          type: node.data('type'),
          department: node.data('department'),
          category: node.data('category'),
          status: node.data('status'),
          skills: node.data('skills'),
          clusterId: node.data('clusterId')
        });
      });

      cy.on('tap', (evt) => {
        if (evt.target === cy) {
          setSelectedNode(null);
        }
      });

      cyRef.current = cy;
    } catch (err) {
      console.error('Error initializing Cytoscape graph:', err);
    }

    return () => {
      if (cyRef.current) {
        try {
          cyRef.current.destroy();
        } catch (e) {
          // Ignore destruction errors
        }
        cyRef.current = null;
      }
    };
  }, [filteredData, isDarkMode, activeTab]);

  const zoomIn = () => {
    if (cyRef.current) cyRef.current.zoom(cyRef.current.zoom() * 1.2);
  };

  const zoomOut = () => {
    if (cyRef.current) cyRef.current.zoom(cyRef.current.zoom() / 1.2);
  };

  const resetView = () => {
    if (cyRef.current) {
      cyRef.current.fit();
      cyRef.current.center();
    }
  };

  // Keep cytoscape sized correctly when window resizes or fullscreen toggles
  useEffect(() => {
    const handleResize = () => {
      if (cyRef.current) {
        cyRef.current.resize();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (cyRef.current) {
      const timer = setTimeout(() => {
        cyRef.current?.resize();
        cyRef.current?.fit(undefined, 30);
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [isFullscreen]);

  // Protect unauthenticated users (after all hooks)
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className={`space-y-6 transition-colors duration-200 ${
      isDarkMode ? 'text-white' : 'text-light-950'
    }`}>
      {/* Top Header & Admin Mode Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold">Graph & Cluster Explorer</h1>
            {isAdmin ? (
              <span className="bg-purple-500/20 text-purple-400 border border-purple-500/30 text-xs px-2.5 py-1 rounded-full font-bold flex items-center space-x-1">
                <Shield className="h-3.5 w-3.5" />
                <span>Admin View Active</span>
              </span>
            ) : (
              <span className="bg-blue-500/20 text-blue-400 border border-blue-500/30 text-xs px-2.5 py-1 rounded-full font-medium">
                Participant Mode
              </span>
            )}
          </div>
          <p className={`mt-1 text-sm ${isDarkMode ? 'text-dark-300' : 'text-light-600'}`}>
            Explore the network connections, skill clusters, and team coverage across organization projects.
          </p>
        </div>

        {/* Tab Selection */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setActiveTab('graph')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center space-x-2 transition-all ${
              activeTab === 'graph'
                ? 'bg-primary-600 text-white shadow-lg'
                : isDarkMode ? 'bg-dark-800 text-dark-300 hover:text-white' : 'bg-light-200 text-light-700'
            }`}
          >
            <Layers className="h-4 w-4" />
            <span>Interactive Graph</span>
          </button>

          <button
            onClick={() => setActiveTab('clusters')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center space-x-2 transition-all ${
              activeTab === 'clusters'
                ? 'bg-purple-600 text-white shadow-lg'
                : isDarkMode ? 'bg-dark-800 text-dark-300 hover:text-white' : 'bg-light-200 text-light-700'
            }`}
          >
            <PieChart className="h-4 w-4" />
            <span>Admin Skill Clusters ({clustersData.length})</span>
          </button>
        </div>
      </div>

      {/* ADMIN SKILL CLUSTERS TAB */}
      {activeTab === 'clusters' && (
        <div className="space-y-6">
          {/* Admin Banner */}
          <div className={`p-4 rounded-xl border flex items-center justify-between ${
            isDarkMode ? 'bg-purple-500/10 border-purple-500/30 text-purple-300' : 'bg-purple-50 border-purple-200 text-purple-900'
          }`}>
            <div className="flex items-center space-x-3">
              <Shield className="h-6 w-6 text-purple-400 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-base">Admin Skill Graph & Community Clusters</h3>
                <p className="text-xs opacity-90">
                  Automated cluster detection identifies skill communities, supply vs. demand gaps, and bridge skills across teams and projects.
                </p>
              </div>
            </div>
            {!isAdmin && (
              <span className="text-xs px-2.5 py-1 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30 font-medium flex-shrink-0">
                Admin Feature View
              </span>
            )}
          </div>

          {/* Clusters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {clustersData.map((cluster) => (
              <div
                key={cluster.id}
                className={`card border transition-all duration-200 ${
                  isDarkMode ? 'dark border-dark-700 hover:border-dark-600' : 'light border-light-200 hover:border-light-300'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-bold border ${
                      cluster.id === 'cluster_frontend' ? (isDarkMode ? 'bg-blue-500/20 border-blue-500/30 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700') :
                      cluster.id === 'cluster_backend' ? (isDarkMode ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300' : 'bg-emerald-50 border-emerald-200 text-emerald-700') :
                      cluster.id === 'cluster_ai' ? (isDarkMode ? 'bg-purple-500/20 border-purple-500/30 text-purple-300' : 'bg-purple-50 border-purple-200 text-purple-700') :
                      cluster.id === 'cluster_qa' ? (isDarkMode ? 'bg-pink-500/20 border-pink-500/30 text-pink-300' : 'bg-pink-50 border-pink-200 text-pink-700') :
                      (isDarkMode ? 'bg-amber-500/20 border-amber-500/30 text-amber-300' : 'bg-amber-100 border-amber-300 text-amber-900 font-semibold')
                    }`}>
                      {cluster.category}
                    </span>
                    <h3 className="text-lg font-bold mt-2">{cluster.name}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">Coverage</div>
                    <div className="text-xl font-extrabold text-primary-400">{cluster.supplyDemandRatio}</div>
                  </div>
                </div>

                <p className={`text-xs mb-4 ${isDarkMode ? 'text-dark-300' : 'text-light-600'}`}>
                  {cluster.description}
                </p>

                {/* Cluster Skills */}
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-400 mb-2">Cluster Core Skills:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {cluster.skills.map(sk => (
                      <span
                        key={sk}
                        className={`text-xs px-2 py-0.5 rounded font-medium ${
                          isDarkMode ? 'bg-dark-800 text-dark-300' : 'bg-light-100 text-light-700'
                        }`}
                      >
                        {sk}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Stats & Missing Skills */}
                <div className={`pt-3 border-t grid grid-cols-2 gap-2 text-xs ${
                  isDarkMode ? 'border-gray-700/40 text-gray-300' : 'border-gray-200 text-gray-600'
                }`}>
                  <div>
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Members in Cluster: </span>
                    <strong className={isDarkMode ? 'text-white' : 'text-gray-950 font-bold'}>{cluster.peopleCount}</strong>
                  </div>
                  <div>
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Project Demand: </span>
                    <strong className={isDarkMode ? 'text-white' : 'text-gray-950 font-bold'}>{cluster.projectDemand} skills needed</strong>
                  </div>
                </div>

                {cluster.topMissingSkills && cluster.topMissingSkills.length > 0 && (
                  <div className={`mt-3 p-2.5 rounded border text-xs flex items-center space-x-2 ${
                    isDarkMode 
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' 
                      : 'bg-amber-100/90 border-amber-300 text-amber-950 font-medium'
                  }`}>
                    <AlertCircle className={`h-4 w-4 flex-shrink-0 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`} />
                    <span>
                      <strong className={isDarkMode ? 'text-amber-200 font-bold' : 'text-amber-950 font-bold'}>
                        Program Skill Gaps:
                      </strong>{' '}
                      Need more participants with {cluster.topMissingSkills.join(', ')}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Admin Program Macro Statistics */}
          <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
            <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
              <BarChart2 className="h-5 w-5 text-purple-400" />
              <span>Program Admin Network Macro Insights</span>
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <div className="text-2xl font-bold text-blue-400">{macroStats.skillClusters}</div>
                <div className="text-xs text-gray-400 mt-1">Skill Clusters</div>
              </div>

              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="text-2xl font-bold text-green-400">{macroStats.activeParticipants}</div>
                <div className="text-xs text-gray-400 mt-1">Active Participants</div>
              </div>

              <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <div className="text-2xl font-bold text-purple-400">{macroStats.networkDensity}</div>
                <div className="text-xs text-gray-400 mt-1">Network Connectivity Density</div>
              </div>

              <div className={`p-3 rounded-lg border ${
                isDarkMode ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'
              }`}>
                <div className={`text-2xl font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-800'}`}>{macroStats.bridgeSkills}</div>
                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Highest Demand Bridge Skills</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GRAPH TAB */}
      {activeTab === 'graph' && (
        <>
          {/* Controls */}
          <div className={`card transition-all duration-200 ${isDarkMode ? 'dark' : 'light'}`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Node Type Filters */}
              <div className="flex items-center space-x-4 text-sm">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.showPeople}
                    onChange={(e) => setFilters(prev => ({ ...prev, showPeople: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>People</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.showSkills}
                    onChange={(e) => setFilters(prev => ({ ...prev, showSkills: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>Skills</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.showProjects}
                    onChange={(e) => setFilters(prev => ({ ...prev, showProjects: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>Projects</span>
                </label>
              </div>

              {/* Cluster Focus Filter */}
              <div className="flex items-center space-x-3 text-sm">
                <span className="text-xs text-gray-400">Cluster Focus:</span>
                <select
                  value={selectedClusterFilter}
                  onChange={(e) => setSelectedClusterFilter(e.target.value)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    isDarkMode ? 'bg-dark-800 border-dark-700 text-white' : 'bg-white border-light-300'
                  }`}
                >
                  <option value="all">All Clusters ({graphData?.nodes?.length || 0} nodes)</option>
                  {clustersData.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Zoom & Screen Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={zoomIn}
                  className={`p-2 rounded-lg font-bold ${
                    isDarkMode ? 'bg-dark-800 hover:bg-dark-700 text-white' : 'bg-light-200 hover:bg-light-300'
                  }`}
                  title="Zoom In"
                >
                  +
                </button>
                <button
                  onClick={zoomOut}
                  className={`p-2 rounded-lg font-bold ${
                    isDarkMode ? 'bg-dark-800 hover:bg-dark-700 text-white' : 'bg-light-200 hover:bg-light-300'
                  }`}
                  title="Zoom Out"
                >
                  −
                </button>
                <button
                  onClick={resetView}
                  className={`p-2 rounded-lg font-bold ${
                    isDarkMode ? 'bg-dark-800 hover:bg-dark-700 text-white' : 'bg-light-200 hover:bg-light-300'
                  }`}
                  title="Fit & Reset View"
                >
                  ⌂
                </button>
                <button
                  onClick={() => setIsFullscreen(prev => !prev)}
                  className={`p-2 rounded-lg font-bold transition-all ${
                    isFullscreen
                      ? 'bg-primary-600 text-white shadow-lg'
                      : isDarkMode ? 'bg-dark-800 hover:bg-dark-700 text-white' : 'bg-light-200 hover:bg-light-300'
                  }`}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fill Entire Screen'}
                >
                  {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Graph Visualization Canvas */}
          <div className={`${
            isFullscreen
              ? 'fixed inset-0 z-50 p-6 bg-dark-950 flex flex-col'
              : 'card p-0 overflow-hidden relative border rounded-2xl w-full h-[calc(100vh-270px)] min-h-[580px] shadow-2xl transition-all'
          } ${
            isDarkMode ? 'dark border-dark-800' : 'light border-light-200'
          }`}>
            {isFullscreen && (
              <div className="flex items-center justify-between pb-3 mb-2 border-b border-dark-800 flex-shrink-0">
                <div className="flex items-center space-x-3">
                  <h2 className="font-bold text-lg text-white">Full-Screen Graph Explorer</h2>
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-primary-500/20 text-primary-300 border border-primary-500/30">
                    {filteredData?.nodes?.length || 0} Nodes
                  </span>
                </div>
                <button
                  onClick={() => setIsFullscreen(false)}
                  className="px-3.5 py-1.5 rounded-lg bg-dark-800 hover:bg-dark-700 border border-dark-700 text-xs font-semibold flex items-center space-x-1.5 text-white transition-all shadow-md"
                >
                  <Minimize2 className="h-4 w-4" />
                  <span>Exit Fullscreen</span>
                </button>
              </div>
            )}

            <div
              ref={containerRef}
              className="w-full h-full flex-1 relative"
              style={{
                backgroundColor: isDarkMode ? '#0a0f1a' : '#f8fafc',
                minHeight: isFullscreen ? '0' : '580px',
                position: 'relative'
              }}
            />

            {/* Floating Node Details Panel */}
            {selectedNode && (
              <div className={`absolute bottom-4 left-4 right-4 md:right-auto md:max-w-md z-20 p-4 rounded-xl border shadow-2xl backdrop-blur-md transition-all animate-fadeIn ${
                isDarkMode ? 'bg-dark-900/90 border-dark-700 text-white' : 'bg-white/95 border-light-300 text-light-950 shadow-xl'
              }`}>
                <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-gray-500/20">
                  <div className="flex items-center space-x-2">
                    <Target className="h-4 w-4 text-primary-400" />
                    <span className="font-bold text-sm">Selected Node Details</span>
                  </div>
                  <button
                    onClick={() => setSelectedNode(null)}
                    className="p-1 rounded-lg hover:bg-gray-500/20 text-gray-400 hover:text-white transition-all text-xs"
                    title="Close Details"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-gray-400 block text-[10px]">Name / Label:</span>
                    <span className="font-bold text-sm text-primary-400 truncate block">{selectedNode.label}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-[10px]">Type:</span>
                    <span className="capitalize font-semibold">{selectedNode.type}</span>
                  </div>
                  {selectedNode.category && (
                    <div>
                      <span className="text-gray-400 block text-[10px]">Category:</span>
                      <span className="font-medium">{selectedNode.category}</span>
                    </div>
                  )}
                  {selectedNode.department && (
                    <div>
                      <span className="text-gray-400 block text-[10px]">Department:</span>
                      <span className="font-medium">{selectedNode.department}</span>
                    </div>
                  )}
                  {selectedNode.status && (
                    <div>
                      <span className="text-gray-400 block text-[10px]">Status:</span>
                      <span className="font-medium">{selectedNode.status}</span>
                    </div>
                  )}
                  {selectedNode.skills && selectedNode.skills.length > 0 && (
                    <div className="col-span-2">
                      <span className="text-gray-400 block text-[10px] mb-1">Associated Skills:</span>
                      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                        {selectedNode.skills.map((sk, i) => (
                          <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-dark-800 text-dark-300 border border-dark-700">
                            {sk}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GraphExplorer;
